import AppUtils from 'src/utils/AppUtils';

const IMAGE_REGEX_PATTERN = /<img[^>]+?src="(data:image\/[^;]+;base64[^"]+)"/gi;

const ERROR_IMAGE_URL = '/images/error-image.png';

/**
 * Extracts all unique base64-encoded image sources from an HTML string,
 * excluding images hosted on Firebase Storage.
 *
 * Moved from OfflineAccessUtils.js
 *
 * @param {string} str - The HTML string to search for base64 image sources.
 * @returns {string[]} An array of unique base64 image source strings.
 */
export function findBase64Images(str) {
  const matches = new Set();

  // Find all external images src
  let match = IMAGE_REGEX_PATTERN.exec(str);
  while (match) {
    // @todo Why exclude Firebase Storage here - they would never match the IMAGE_REGEX_PATTERN?
    if (match && match[1] && !match[1].includes('https://firebasestorage.googleapis.com/')) {
      matches.add(match[1]);
    }

    match = IMAGE_REGEX_PATTERN.exec(str);
  }

  return Array.from(matches);
}

/**
 * Replaces base64-encoded image sources in HTML content with corresponding URLs from a provided map.
 *
 * This function searches for image tags with base64-encoded `src` attributes, computes a hash for each base64 string,
 * and replaces the `src` attribute with a URL from the `urlMap` based on the hash. If no matching URL is found,
 * a default error image URL is used. Other attributes in the image tag are preserved.
 *
 * Remember that urlMap keys are hashes of base64 strings, not the base64 strings themselves.
 *
 * @async
 * @param {string} content - The HTML content containing image tags with base64-encoded sources.
 * @param {Object.<string, string>} urlMap - An object mapping image hashes to their corresponding URLs.
 * @returns {Promise<string>} The updated HTML content with base64 image sources replaced by URLs.
 */
export async function replaceBase64ImagesWithUrls(content, urlMap) {
  // Use async replacer for hashing
  const matches = [];
  let match = IMAGE_REGEX_PATTERN.exec(content);
  while (match !== null) {
    matches.push({
      fullTag: match[0],
      base64Src: match[1],
      index: match.index,
    });
    match = IMAGE_REGEX_PATTERN.exec(content);
  }

  // If no matches, return original content
  if (matches.length === 0) {
    return content;
  }

  // Compute hashes and replacements
  const replacementPromise = matches.map(async ({ fullTag, base64Src }) => {
    const imgHash = await AppUtils.hashString(base64Src);
    const url = urlMap[imgHash] || ERROR_IMAGE_URL;
    // Replace src attribute only, preserve other attributes
    const newTag = fullTag.replace(/src="[^"]+"/, `src="${url}"`);
    return { fullTag, newTag };
  });
  const replacements = await Promise.all(replacementPromise);

  // Replace all in one pass
  let updatedContent = content;
  replacements.forEach(({ fullTag, newTag }) => {
    updatedContent = updatedContent.replace(fullTag, newTag);
  });

  return updatedContent;
} // replaceBase64ImagesWithUrls()
